<script setup lang="ts">
/**
 * The ⓘ beside a field label: hover or keyboard focus opens a short
 * explanation in the kit tooltip. The trigger is a real button, so tabbing
 * reaches it, and the tooltip is interactive — a longer hint can be hovered
 * and its text selected (WCAG 1.4.13).
 *
 * Pass the explanation as `text`, or rich content in the default slot. The
 * button's accessible name is `label` („Hinweis“) followed by the text, so a
 * screen reader hears the hint without opening anything. Nothing renders
 * without content — an ⓘ that opens onto nothing is worse than none.
 *
 * Place it BESIDE the `<label>` (e.g. NxLabel's `#info` slot), never inside:
 * a control inside a label forwards its clicks to the input.
 *
 * `mode="dialog"` is for background that runs to paragraphs — examples, the
 * order rules apply in, legal notes. A click opens a modal (`NxModal`)
 * headed `title`, with the default slot (or `text`) as its body and a close
 * button; the content holds still while it is read, where a tooltip would
 * vanish on the next pointer move. The tooltip then only names the button.
 * Keep operative hints (why a control is disabled) in the page, not in here.
 */
import { InfoIcon } from '@blpde/nx-icons';
import { computed, ref, useSlots } from 'vue';
import { NxButton } from '../Button';
import { NxModal } from '../Modal';
import { NxTooltip, type NxTooltipProps } from '../Tooltip';

const props = withDefaults(
  defineProps<{
    /** The explanation, already translated. The default slot replaces it. */
    text?: string;
    /** Start of the accessible name („Hinweis“). */
    label?: string;
    side?: NxTooltipProps['side'];
    /** `sm` sits beside small captions, `md` beside regular text. */
    size?: 'sm' | 'md';
    /** `tooltip` (default) on hover and focus, `dialog` in a modal on click. */
    mode?: 'tooltip' | 'dialog';
    /** Heading of the dialog — the subject explained. Falls back to `label`. */
    title?: string;
    /** Label of the dialog's close button. */
    closeLabel?: string;
    /**
     * `false` takes the ⓘ out of the Tab order (`tabindex="-1"`): Tab goes from
     * input to input, which a mask captured by keyboard needs. The hint stays
     * on hover, and the accessible name still carries it.
     */
    tabStop?: boolean;
  }>(),
  {
    text: '',
    label: 'Hinweis',
    side: 'top',
    size: 'sm',
    mode: 'tooltip',
    title: '',
    closeLabel: 'Schließen',
    tabStop: true,
  }
);

const slots = useSlots();
const hasContent = computed(() => Boolean(props.text) || Boolean(slots.default));
const isDialog = computed(() => props.mode === 'dialog');
const heading = computed(() => props.title || props.label);
/** Tooltip mode reads the hint itself; dialog mode names what the dialog explains. */
const accessibleName = computed(() => {
  if (isDialog.value) return props.title ? `${props.label}: ${props.title}` : props.label;
  return props.text ? `${props.label}: ${props.text}` : props.label;
});

const open = ref(false);
</script>

<template>
  <template v-if="hasContent && isDialog">
    <NxTooltip :text="heading" :side="props.side">
      <button
        class="nx-info-hint is-dialog"
        :class="`size-${props.size}`"
        type="button"
        :aria-label="accessibleName"
        aria-haspopup="dialog"
        :aria-expanded="open"
        :tabindex="props.tabStop ? undefined : -1"
        data-testid="nx-info-hint"
        @click="open = true"
      >
        <InfoIcon class="nx-info-hint__icon" aria-hidden="true" />
      </button>
    </NxTooltip>
    <NxModal v-model:open="open" :title="heading" size="wide" auto-width>
      <template #body>
        <div class="nx-info-hint__dialog" data-testid="nx-info-hint-dialog">
          <slot>
            <p>{{ props.text }}</p>
          </slot>
        </div>
      </template>
      <template #footer="{ close }">
        <NxButton @click="close">{{ props.closeLabel }}</NxButton>
      </template>
    </NxModal>
  </template>
  <NxTooltip v-else-if="hasContent" :text="props.text" :side="props.side" interactive>
    <button
      class="nx-info-hint"
      :class="`size-${props.size}`"
      type="button"
      :aria-label="accessibleName"
      :tabindex="props.tabStop ? undefined : -1"
      data-testid="nx-info-hint"
    >
      <InfoIcon class="nx-info-hint__icon" aria-hidden="true" />
    </button>
    <template v-if="$slots.default" #content>
      <slot />
    </template>
  </NxTooltip>
</template>

<style scoped>
/* `flex`, not `inline-flex`: NxTooltip wraps the trigger in an inline-block,
   and an inline-level button picks up the line box's descender gap — the icon
   would ride high against the caption beside it. */
.nx-info-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: none;
  color: var(--nxt-fg-muted);
  line-height: 1;
  cursor: help;
  transition:
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-info-hint.is-dialog {
  cursor: pointer;
}
.nx-info-hint.size-sm {
  width: calc(var(--nxt-size-unit) * 1);
  height: calc(var(--nxt-size-unit) * 1);
}
.nx-info-hint.size-md {
  width: calc(var(--nxt-size-unit) * 1.25);
  height: calc(var(--nxt-size-unit) * 1.25);
}
.nx-info-hint__icon {
  width: 100%;
  height: 100%;
}
.nx-info-hint:hover,
.nx-info-hint:focus-visible {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
/* `--nxt-focus-ring` is a colour: it needs an explicit outline style. */
.nx-info-hint:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
/* Read prose: capped at a reading measure (the shell's `auto-width` sizes the
   dialog to it, never below its own floor), and bare <p> from the caller get a
   reading rhythm instead of browser margins. */
.nx-info-hint__dialog {
  max-width: 70ch;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
.nx-info-hint__dialog :deep(p) {
  margin: var(--nxt-space-sm) 0 0;
  /* The global type reset sizes every <p>; the dialog sets the size. */
  font-size: inherit;
}
.nx-info-hint__dialog :deep(p:first-child) {
  margin-top: 0;
}
@media (prefers-reduced-motion: reduce) {
  .nx-info-hint {
    transition: none;
  }
}
</style>
